import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play, Zap, TrendingUp, ArrowDown, Users, Sparkles } from 'lucide-react';
import { heroStats, marqueeItems } from '../data/mock';

const HeroSection = ({ onCtaClick }) => {
  const scrollToHowWeWork = () => {
    const element = document.getElementById('how-we-work');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen relative overflow-hidden bg-slate-950">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-40 w-96 h-96 bg-rose-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob" />
        <div className="absolute top-0 -right-40 w-96 h-96 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIiBkPSJNMCAwaDYwdjYwSDB6Ii8+PHBhdGggZD0iTTYwIDBIMHY2MCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-50" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Column - Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/10">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-white/90">
                Full-Funnel Growth Partner
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight">
              <span className="text-white">We turn</span>
              <br />
              <span className="bg-gradient-to-r from-rose-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
                clicks into
              </span>
              <br />
              <span className="text-white">customers.</span>
            </h1>

            {/* Subheading */}
            <p className="text-xl text-slate-400 max-w-lg leading-relaxed">
              Not just another agency. We're growth architects who build 
              <span className="text-white font-medium"> performance systems </span> 
              that actually work.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4 pt-4">
              <Button
                onClick={onCtaClick}
                className="group bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white px-8 py-6 h-auto text-lg rounded-full font-semibold transition-all hover:scale-105 hover:shadow-2xl hover:shadow-rose-500/30"
              >
                Let's review your funnel
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>

              <button
                onClick={scrollToHowWeWork}
                className="group flex items-center gap-3 text-white/80 hover:text-white font-medium text-lg transition-colors py-3"
              >
                <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Play className="w-5 h-5 fill-current" />
                </span>
                See how it works
              </button>
            </div>

            {/* Stats */}
            <div className="flex gap-8 pt-8 border-t border-white/10">
              {heroStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-black text-white">{stat.value}</div>
                  <div className="text-sm text-slate-500 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Floating Metrics Display */}
          <div className="relative hidden lg:flex items-center justify-center min-h-[500px]">
            {/* Decorative rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-80 h-80 rounded-full border border-rose-500/20 animate-pulse" />
              <div className="absolute w-64 h-64 rounded-full border border-orange-500/20 animate-pulse animation-delay-2000" />
              <div className="absolute w-48 h-48 rounded-full border border-amber-500/20 animate-pulse animation-delay-4000" />
            </div>

            {/* Floating metric cards - positioned around the center */}
            {/* Top card - Conversion Rate */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-emerald-500/30 animate-float shadow-lg shadow-emerald-500/10 hover:scale-105 transition-transform cursor-default">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-black text-emerald-400">+247%</div>
                  <div className="text-xs md:text-sm text-white/70 font-medium">Conversion rate</div>
                </div>
              </div>
            </div>

            {/* Left card - CAC Reduced */}
            <div className="absolute left-0 top-1/2 -translate-y-1/2 bg-gradient-to-br from-rose-500/20 to-pink-500/20 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-rose-500/30 animate-float animation-delay-2000 shadow-lg shadow-rose-500/10 hover:scale-105 transition-transform cursor-default">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-rose-500/20 flex items-center justify-center">
                  <ArrowDown className="w-5 h-5 md:w-6 md:h-6 text-rose-400" />
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-black text-rose-400">-34%</div>
                  <div className="text-xs md:text-sm text-white/70 font-medium">CAC reduced</div>
                </div>
              </div>
            </div>

            {/* Right card - ROAS */}
            <div className="absolute right-0 top-1/3 bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30 animate-float animation-delay-4000 shadow-lg shadow-amber-500/10 hover:scale-105 transition-transform cursor-default">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <div className="text-3xl font-black text-amber-400">3.2x</div>
                  <div className="text-sm text-white/70 font-medium">ROAS achieved</div>
                </div>
              </div>
            </div>

            {/* Bottom card - Lead Volume */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/4 bg-gradient-to-br from-sky-500/20 to-cyan-500/20 backdrop-blur-xl rounded-2xl p-4 md:p-6 border border-sky-500/30 animate-float animation-delay-3000 shadow-lg shadow-sky-500/10 hover:scale-105 transition-transform cursor-default">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-sky-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 md:w-6 md:h-6 text-sky-400" />
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-black text-sky-400">2.1x</div>
                  <div className="text-xs md:text-sm text-white/70 font-medium">Lead volume</div>
                </div>
              </div>
            </div>

            {/* Center element - small and non-blocking */}
            <div className="relative z-0">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center animate-pulse shadow-2xl shadow-rose-500/30">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 py-4 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-8">
          {[...marqueeItems, ...marqueeItems].map((item, index) => (
            <span key={index} className="text-white font-bold text-lg">
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
